/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Debounce from 'lodash/debounce' // Npm: Lodash library.
import Moment from 'moment' // Npm: Moment.js library.
import _ from 'underscore' // Npm: Underscore.js library.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import DownloadToExcel from 'components/DownloadToExcel'
import Modal from 'components/Modal'
import EntityUpsert from 'components/EntityUpsert'
import EntityDelete from 'components/EntityDelete'
import EntityDropDownOptions from 'components/EntityDropDownOptions'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import EntityReadQuery from './__query__/index.entity.read.query'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({
    'items': []
  })
  const [searchQuery, setSearchQuery] = React.useState('')

  const { 'onOpen': onEntityUpdateOpen, 'isOpen': isEntityUpdateOpen, 'onClose': onEntityUpdateClose } = useDisclosure()
  const { 'onOpen': onEntityDeleteOpen, 'isOpen': isEntityDeleteOpen, 'onClose': onEntityDeleteClose } = useDisclosure()
  const { 'onOpen': onEntityCreateOpen, 'isOpen': isEntityCreateOpen, 'onClose': onEntityCreateClose } = useDisclosure()

  // Debounced filter handler
  const debouncedFilterHandler = React.useRef(
    _.debounce((newFilterModel) => {
      const addressFilter = newFilterModel?.items?.find(item => item.field === 'address')
      setSearchQuery(addressFilter?.value || '')
    }, 800)
  ).current

  React.useEffect(() => {
    return () => {
      debouncedFilterHandler.cancel?.()
    }
  }, [])

  // Handle filter model change
  const handleFilterModelChange = React.useCallback((newFilterModel) => {
    setFilterModel(newFilterModel)
    debouncedFilterHandler(newFilterModel)
  }, [])

  const _QueryEntityRead = useQuery(EntityReadQuery, {
    'variables': {
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize,
      'search': searchQuery
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data assignment.
  if (!_QueryEntityRead.loading && 0 < _QueryEntityRead?.data?.EntityRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryEntityRead?.data?.EntityRead?.length || !_.every(_.pluck(_QueryEntityRead?.data?.EntityRead, 'status'), j => _successFlags.includes(j))) _isFirstLoadCompleted.current = false

  // // Columns definition
  const columns = React.useMemo(() => [
    {
      field: 'sno',
      headerName: 'S No.',
      align: 'center',
      headerAlign: 'center',
      width: 80,
      disableColumnMenu: true,
      sortable: false,
      filterable: false
    },
    {
      field: 'id',
      headerName: 'ID',
      align: 'center',
      editable: false,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      width: 100
    },
    {
      field: 'displayName',
      headerName: 'Name',
      description: 'Entity name.',
      editable: true,
      sortable: true,
      filterable: true,
      align: 'center',
      headerAlign: 'center',
      flex: 1.2,
      minWidth: 170
    },
    {
      field: 'address',
      headerName: 'Address',
      description: 'Entity address.',
      editable: true,
      sortable: true,
      filterable: true,
      align: 'center',
      headerAlign: 'center',
      flex: 1.5,
      minWidth: 170
    },
    {
      field: 'CreatedAt',
      headerName: 'Created At',
      description: 'Creation date.',
      editable: false,
      sortable: false,
      filterable: false,
      align: 'center',
      disableColumnMenu: true,
      headerAlign: 'center',
      flex: 0.8,
      minWidth: 150
    },
    {
      field: 'UpdatedAt',
      headerName: 'Updated At',
      description: 'Last update date.',
      editable: false,
      sortable: false,
      filterable: false,
      align: 'center',
      disableColumnMenu: true,
      headerAlign: 'center',
      flex: 0.8,
      minWidth: 150
    },
    {
      field: 'action',
      headerName: 'Actions',
      description: 'Actions available.',
      editable: false,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      width: 100,
      renderCell: ({ row }) => (
        <EntityDropDownOptions
          entityId={row.originalId}
          onEntityUpdate={onEntityUpdateOpen}
          onEntityDelete={onEntityDeleteOpen}
        />
      )
    }
  ], [])

  // Rows data
  const rows = React.useMemo(() => _.compact(_QueryEntityRead?.data?.EntityRead?.map((item, index) =>
    _successFlags.includes(item.status) ? ({
      'id': item?.id || `entity-${index}-${Date.now()}`,
      'originalId': item?.id,
      'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
      'displayName': item?.displayName || '-',
      'address': item?.address || '-',
      'CreatedAt': Moment().format('YYYY-MM-DD'),
      'UpdatedAt': Moment().format('YYYY-MM-DD'),
    }) : null
  )), [_QueryEntityRead?.data?.EntityRead, paginationModel.page, paginationModel.pageSize])
  // Return Component.
  return (
    <>
      <Flex className='entity base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          position='relative'
          gap={_isCurrentViewMobile ? '12px' : '14px'}
          bg='white'
          minH='82vh'
          maxH='82vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' gap='22px' justify='space-between'>
            <Tooltip label='Create new entity.' fontSize='sm'>
              <Button
                w='170px'
                onClick={onEntityCreateOpen}
                leftIcon={<IoMdAdd />}
                color="rgb(92, 55, 255)"
                fontFamily="DM Sans"
                fontWeight="500"
                fontSize="16px"
                borderRadius="10px"
                className='addButton'>
                New Entity
              </Button>
            </Tooltip>
            <DownloadToExcel
              cellsData={_QueryEntityRead?.data?.EntityRead?.map(
                (item, __index) =>
                  _successFlags.includes(item.status) ? {
                    'S.No.': `${__index + (paginationModel.pageSize * paginationModel.page) + 1}.`,
                    'Entity Name': item.displayName ?? '-',
                    'Address': item.address ?? '-',
                    'CreatedAt': Moment().format('YYYY-MM-DD'),
                    'UpdatedAt': Moment().format('YYYY-MM-DD'),
                  } : {}
              )}
              headersData={[
                'S.No.',
                'Entity Name',
                'Address',
                'CreatedAt',
                'UpdatedAt'
              ].map(k => ({ 'key': k, 'label': k }))}
            />
          </Flex>

          {/* MUI DataGrid */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryEntityRead?.data?.EntityRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            filterMode='server'
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryEntityRead?.loading}
            disableColumnMenu={false}
          />
        </Flex>

        {/* Modals */}
        <Modal
          size='lg'
          title='Entity Create'
          isOpen={isEntityCreateOpen}
          onClose={onEntityCreateClose}
          isCentered={false}
        >
          <EntityUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='lg'
          title='Entity Update'
          isOpen={isEntityUpdateOpen}
          onClose={onEntityUpdateClose}
          isCentered={false}
        >
          <EntityUpsert />
        </Modal>
        <Modal
          size='md'
          title='Entity Delete'
          isOpen={isEntityDeleteOpen}
          onClose={onEntityDeleteClose}
        >
          <EntityDelete />
        </Modal>
      </Flex>
    </>
  )
}

Index.propTypes = {}


/*
 * EXPORTS
 */
export default Index
